/**
 * Schémas Zod — scoring de comparaison (chunk 9.AJ.1).
 *
 * Source DB : `app.comparison_criteria_weights` (migration 0073).
 *
 * ## Ce qui se règle, et ce qui ne se règle pas
 *
 * Se règle : le **poids** d'un critère, et son **activation**. C'est une préférence
 * de cabinet.
 *
 * Ne se règle pas : le **sens** d'un critère — « plus haut vaut mieux » ou « plus
 * bas vaut mieux ». C'est de la connaissance métier, elle vit au registre shared, et
 * l'exposer en écriture offrirait un réglage capable d'inverser silencieusement tous
 * les classements. Le sens est donc rendu en LECTURE seule, pour que le conseiller
 * voie dans quel sens chaque critère est lu.
 *
 * Ne se stocke pas : le score, le rang, la valeur comparée. Tout se recalcule depuis
 * la même lecture que l'écran comparateur.
 */

import { z } from "zod";

/** Le format canonique d'un `field_key`, aligné sur la CHECK de la 0073. */
const FieldKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_.]*$/, "field_key must match canonical format")
  .min(2)
  .max(80);

/**
 * Un réglage tel qu'il revient au wire : ce que le cabinet a décidé, PLUS le sens
 * et la provenance, en lecture seule.
 */
export const CriterionWeightRowSchema = z.object({
  field_key: FieldKeySchema,
  /** Lecture seule — connaissance métier, jamais un réglage. */
  direction: z.enum(["higher_is_better", "lower_is_better"]),
  guarantee_family: z.string().nullable(),
  weight: z.number().min(0).max(1000),
  enabled: z.boolean(),
  /**
   * D'où vient la valeur effective : réglage du cabinet, défaut Kollectivia, ou
   * déclaration du registre. Le dire évite de croire qu'un cabinet a décidé
   * quelque chose qu'il n'a jamais touché.
   */
  source: z.enum(["tenant", "global", "registry"]),
});
export type CriterionWeightRow = z.infer<typeof CriterionWeightRowSchema>;

/**
 * Écriture d'un lot de réglages.
 *
 * Un lot plutôt qu'un réglage à la fois : les poids n'ont de sens que les uns par
 * rapport aux autres, et enregistrer critère par critère ferait passer le cabinet
 * par une série d'états intermédiaires dont aucun n'est celui qu'il voulait.
 */
export const UpdateCriterionWeightsSchema = z.object({
  settings: z
    .array(
      z.object({
        field_key: FieldKeySchema,
        weight: z.number().min(0).max(1000),
        enabled: z.boolean(),
      }),
    )
    .min(1)
    .max(200),
});
export type UpdateCriterionWeightsInput = z.infer<typeof UpdateCriterionWeightsSchema>;
